import React, { useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import opsService from '../../../services/opsService';

function Card({ label, value, sub }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
      <div className="text-ink-muted text-small">{label}</div>
      <div className="text-2xl font-bold text-ink-soft">{value}</div>
      {sub && <div className="text-ink-muted text-small mt-1">{sub}</div>}
    </div>
  );
}

const money = (n, cur = 'CAD') => `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OpsExpenses() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    opsService.expensesSummary().then(setData).catch((e) =>
      setError(e?.response?.data?.detail || 'Failed to load expenses'));
  }, []);

  if (error) return <div className="text-danger p-6">{error}</div>;
  if (!data) return <div className="text-ink-muted p-6">Loading expenses…</div>;

  const cur = data.currencies?.[0]?.currency || 'CAD';
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-ink-soft flex items-center gap-2">
        <Receipt className="w-5 h-5" /> Expenses
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Committed spend" value={money(data.committed_total, cur)} sub={`${data.committed_count} invoices`} />
        <Card label="Avg invoice" value={money(data.avg_invoice, cur)} />
        <Card label="Awaiting review" value={money(data.pending_total, cur)} sub={`${data.pending_count} pending`} />
        <Card label="Open orders" value={money(data.open_orders_total, cur)} sub={`${data.open_orders_count} open`} />
      </div>

      <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
        <div className="font-medium text-ink-soft mb-2">Spend by month</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.by_month}>
            <XAxis dataKey="month" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip formatter={(v) => money(v, cur)} />
            <Bar dataKey="total" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
        <div className="px-4 py-2 font-medium text-ink-soft border-b">Spend by supplier</div>
        <table className="w-full text-small">
          <thead className="bg-surface-muted text-ink-muted">
            <tr><th className="text-left px-4 py-2">Supplier</th><th className="text-left px-4 py-2">Invoices</th><th className="text-right px-4 py-2">Total</th></tr>
          </thead>
          <tbody>
            {data.by_supplier.map((s) => (
              <tr key={s.supplier_id ?? 'unassigned'} className="border-t">
                <td className="px-4 py-2 text-ink-soft">{s.supplier_name}</td>
                <td className="px-4 py-2">{s.invoice_count}</td>
                <td className="px-4 py-2 text-right">{money(s.total, cur)}</td>
              </tr>
            ))}
            {data.by_supplier.length === 0 && (
              <tr><td colSpan="3" className="px-4 py-6 text-center text-ink-muted">No committed expenses in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data.currencies?.length > 1 && (
        <div className="text-ink-muted text-small">Multiple currencies present: {data.currencies.map((c) => `${c.currency} ${Number(c.total ?? 0).toFixed(2)}`).join(' · ')}</div>
      )}
    </div>
  );
}
